
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, setDoc, updateDoc, arrayUnion, arrayRemove, collection, query, orderBy, onSnapshot, where } from 'firebase/firestore'; 
import { db } from '../../firebase';
import { User, Calendar, LogOut, X, Palette, Star, Trash2, Wand2, Edit, PlusCircle, Bell, ChevronDown, Settings } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GiBattleAxe } from 'react-icons/gi';
import { BiSolidCalendarCheck } from 'react-icons/bi';
import { useNotification } from 'contexts/NotificationContext';
import { COLOR_PRESETS, COLOR_CATEGORIES } from 'constants/colorThemes.js';

const hexToRgb = (hex) => {
  if (!hex) return [0, 0, 0];
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
};

const getContrastingTextColor = (rgb) => {
  if (!rgb) return 'text-gray-800';
  const yiq = ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000;
  return (yiq >= 128) ? 'text-gray-800' : 'text-white';
};

const FireIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500 drop-shadow-sm filter" fill="currentColor" viewBox="0 0 24 24" stroke="none"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.657 7.343A8 8 0 0117.657 18.657z" /><path strokeLinecap="round" strokeLinejoin="round" d="M9.343 17.657a8 8 0 011.414-1.414" /></svg>;

export const ColorSelectorModal = ({
  isOpen,
  onClose,
  currentColor,
  onColorChange,
  customThemes,
  favoriteThemes,
  onEditTheme,
  onAddNewTheme,
  onRemoveCustomTheme,
  onToggleFavorite,
  isDarkMode
}) => {

  if (!isOpen) return null;

  const ThemeButton = ({ theme, isCustom = false }) => {
    const isFavorite = favoriteThemes.includes(theme.id);
    const previewStyle = isCustom ? { backgroundColor: theme.color } : {};
    const previewClass = isCustom ? '' : theme.preview;

    return (
      <div className="relative group">
        <button
          onClick={() => onColorChange(theme.id)}
          className={`w-full p-3 rounded-2xl border-2 transition-all duration-200 hover:scale-105 active:scale-95 ${
            currentColor === theme.id
              ? 'border-indigo-500 ring-2 ring-indigo-200 dark:border-indigo-400 dark:ring-indigo-900'
              : 'border-gray-100 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
          }`}
        >
          <div className={`w-full h-8 rounded-xl mb-2 shadow-sm ${previewClass}`} style={previewStyle}></div>
          <span className="text-xs font-bold text-gray-600 dark:text-gray-300 block truncate">{theme.name}</span>
        </button>
        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(theme.id); }} className="p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-700 backdrop-blur-sm shadow-sm transition-colors">
            <Star size={12} className={isFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-400'} />
          </button>
          {isCustom && (
            <>
              <button onClick={(e) => { e.stopPropagation(); onEditTheme(theme); }} className="p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-700 backdrop-blur-sm shadow-sm transition-colors">
                <Edit size={12} className="text-blue-500" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onRemoveCustomTheme(theme.id); }} className="p-1.5 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-700 backdrop-blur-sm shadow-sm transition-colors">
                <Trash2 size={12} className="text-red-500" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-gray-100 dark:border-gray-800"
      >
        <div className="flex items-center justify-between mb-8 sticky top-0 bg-white dark:bg-gray-900 z-10 py-2 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-xl font-extrabold text-gray-900 dark:text-white flex items-center">
            <Palette className="mr-3 h-6 w-6 text-indigo-500" />
            テーマカラー
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-8">
            {favoriteThemes.length > 0 && (
            <div>
                <h4 className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-4 flex items-center uppercase tracking-wider">
                <Star size={16} className="mr-2 text-amber-400 fill-amber-400" />
                お気に入り
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {favoriteThemes.map(themeId => {
                    const preset = COLOR_PRESETS[themeId];
                    const custom = customThemes.find(t => t.id === themeId);
                    const theme = preset ? { id: themeId, ...preset } : custom;
                    if (!theme) return null;
                    return <ThemeButton key={theme.id} theme={theme} isCustom={!!custom} />;
                })}
                </div>
            </div>
            )}

            <div>
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold text-gray-500 dark:text-gray-400 flex items-center uppercase tracking-wider">
                <Wand2 size={16} className="mr-2 text-purple-500" />
                カスタムテーマ
                </h4>
                <button onClick={onAddNewTheme} className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1.5 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors">
                <PlusCircle size={14} />
                新規作成
                </button>
            </div>
            {customThemes.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {customThemes.map(theme => (
                    <ThemeButton key={theme.id} theme={theme} isCustom={true} />
                ))}
                </div>
            ) : (
                <div className="text-center py-8 text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                <p className="text-sm">自分だけのテーマを作ってみよう</p>
                </div>
            )}
            </div>

            {Object.entries(COLOR_CATEGORIES).map(([categoryKey, category]) => {
            const themes = Object.entries(COLOR_PRESETS).filter(([_, preset]) => preset.category === categoryKey);
            if (themes.length === 0) return null;

            return (
                <div key={categoryKey}>
                <h4 className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-4 flex items-center uppercase tracking-wider">
                    <span className="mr-2 text-gray-400 dark:text-gray-500">{category.icon}</span>
                    {category.name}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {themes.map(([key, preset]) => (
                    <ThemeButton key={key} theme={{ id: key, ...preset }} />
                    ))}
                </div>
                </div>
            );
            })}
        </div>
      </motion.div>
    </div>
  );
};

const Header = ({ user, onLogout, showCountdown, countdownHeight, setCountdownHeight, onStreakClick }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentColorTheme, setCurrentColorTheme] = useState('blue');
  const [customThemes, setCustomThemes] = useState([]);
  const [favoriteThemes, setFavoriteThemes] = useState([]);
  const navigate = useNavigate();
  const menuRef = useRef();
  const [countdownEvents, setCountdownEvents] = useState([]);
  const [loadingCountdowns, setLoadingCountdowns] = useState(true);
  const countdownAreaRef = useRef(null);
  const streak = user?.currentStreak || 0;

  useEffect(() => {
    if (user && !user.isAnonymous) {
      const userRef = doc(db, 'users', user.uid);
      const unsubscribe = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setCurrentColorTheme(userData.headerColorTheme || 'blue');
          setCustomThemes(userData.customThemes || []);
          setFavoriteThemes(userData.favoriteThemes || []);
        }
      });
      return () => unsubscribe();
    }
  }, [user]);

  const isCustomTheme = currentColorTheme?.startsWith('custom_');
  const customThemeData = isCustomTheme ? customThemes.find(t => t.id === currentColorTheme) : null;

  let headerStyle = {};
  let currentColors;

  if (customThemeData) {
    const { type, color1, color2, opacity, isGlass } = customThemeData;
    const rgb1 = hexToRgb(color1);
    const textColorClass = getContrastingTextColor(rgb1);

    if (type === 'solid') {
      headerStyle = { backgroundColor: `rgba(${rgb1.join(', ')}, ${opacity})` };
    } else {
      const rgb2 = hexToRgb(color2);
      headerStyle = { background: `linear-gradient(to right, rgba(${rgb1.join(', ')}, ${opacity}), rgba(${rgb2.join(', ')}, ${opacity}))` };
    }
    
    currentColors = {
      ...COLOR_PRESETS.gray, 
      header: `${textColorClass} ${isGlass ? 'backdrop-blur-lg' : ''}`,
      userIcon: textColorClass,
      streak: textColorClass === 'text-white'
        ? 'bg-white/15 hover:bg-white/25'
        : 'bg-gray-100/70 hover:bg-gray-200/70',
      userMenu: textColorClass === 'text-white'
        ? 'bg-white/15 hover:bg-white/25'
        : 'bg-gray-100/50 hover:bg-gray-200/50',
      avatar: textColorClass === 'text-white'
        ? 'bg-white/25 ring-white/40'
        : 'bg-gray-200 ring-gray-300',
      border: textColorClass === 'text-white'
        ? 'border-white/20'
        : 'border-gray-200',
    };

  } else {
    currentColors = COLOR_PRESETS[currentColorTheme] || COLOR_PRESETS.blue;
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setLoadingCountdowns(true);
    const q = query(collection(db, 'countdownEvents'), orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = new Date();
      const events = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          label: data.label,
          date: data.date.toDate ? data.date.toDate() : data.date
        };
      }).filter(event => {
        const targetDate = new Date(event.date);
        return targetDate.getTime() > now.getTime();
      });
      setCountdownEvents(events);
      setLoadingCountdowns(false);
    }, (error) => {
      console.error("Error fetching countdown events: ", error);
      setLoadingCountdowns(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (countdownAreaRef.current) {
        setCountdownHeight(countdownAreaRef.current.offsetHeight);
    } else {
        setCountdownHeight(0);
    }
  }, [countdownEvents, loadingCountdowns, showCountdown, setCountdownHeight]);

  const getCountdownText = useCallback((event) => {
    if (!event) return null;
    const targetDate = new Date(event.date);
    const now = new Date();
    const difference = targetDate.getTime() - now.getTime();
    if (difference <= 0) {
      return { days: 0, text: `${event.label} 終了` };
    }
    const days = Math.ceil(difference / (1000 * 60 * 60 * 24));
    return { days, text: `${event.label}まであと ${days} 日` };
  }, []);

  return (
    <>
      <header className={`fixed inset-x-0 top-0 z-[60] ${currentColors.header} shadow-sm backdrop-blur-md h-[72px] border-b ${currentColors.border} transition-colors duration-300`} style={headerStyle}>
        <div className="container mx-auto px-4 flex justify-between items-center h-full max-w-7xl">
          <Link to="/" className="flex items-center transition-transform hover:scale-105 active:scale-95 duration-200">
            <div className="flex items-center gap-2.5">
                <div className="relative">
                    <GiBattleAxe className="h-7 w-7 sm:h-8 sm:w-8 drop-shadow-sm" />
                    <div className="absolute inset-0 bg-white/20 blur-lg rounded-full -z-10 animate-pulse"></div>
                </div>
                <h1 className="text-lg sm:text-2xl font-black tracking-tight leading-none">
                  Study Quest
                </h1>
            </div>
          </Link>

          {user && (
            <div className="flex items-center space-x-2 sm:space-x-3">
              {streak > 0 && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center space-x-1.5 ${currentColors.streak} px-3 py-1.5 rounded-full cursor-pointer transition-all border border-white/10 hidden sm:flex`}
                  onClick={onStreakClick}
                >
                  <FireIcon />
                  <span className="font-bold text-sm tracking-wide">{streak}</span>
                </motion.div>
              )}

              {/* Notification Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`relative flex items-center justify-center w-10 h-10 rounded-full ${currentColors.userMenu} transition-colors duration-200`}
                onClick={() => navigate('/notifications')}
                aria-label="通知"
              >
                <Bell className={`w-5 h-5 ${currentColors.userIcon}`} />
              </motion.button>
              
              <div className="relative" ref={menuRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center pl-1 pr-1 py-1 rounded-full ${currentColors.userMenu} transition-colors duration-200 gap-2 border border-transparent hover:border-white/20`}
                  onClick={() => setMenuOpen((p) => !p)}
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="プロフィール"
                      className={`w-8 h-8 rounded-full object-cover ring-2 ring-offset-1 ring-offset-transparent ${currentColors.avatar.split(' ')[1]}`} 
                    />
                  ) : (
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full ${currentColors.avatar}`}>
                      <User className={`w-4 h-4 ${currentColors.userIcon}`} /> 
                    </div>
                  )}
                  <ChevronDown size={14} className={`mr-1 opacity-70 ${currentColors.userIcon}`} />
                </motion.button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95, rotateX: -10 }}
                      animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95, rotateX: 10 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      style={{ transformOrigin: "top right" }}
                      className="absolute right-0 mt-3 w-64 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl text-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700/50 z-50 ring-1 ring-black/5"
                    >
                      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                        <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Signed in as</p>
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{user.name || user.email || 'Guest'}</p>
                      </div>
                      
                      <div className="py-2 px-2 space-y-1">
                        <button
                          onClick={() => { navigate('/records'); setMenuOpen(false); }}
                          className="flex items-center w-full text-left px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-200 transition-colors"
                        >
                          <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg mr-3">
                            <User className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          </div>
                          マイページ
                        </button>

                        <button
                          onClick={() => { navigate('/settings', { state: { defaultTab: 'settings' } }); setMenuOpen(false); }}
                          className="flex items-center w-full text-left px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-200 transition-colors"
                        >
                          <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg mr-3">
                            <Settings className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          </div>
                          設定
                        </button>

                        <div className="h-px bg-gray-100 dark:bg-gray-700/50 my-1 mx-2"></div>
                        
                        {!user.isAnonymous && (
                           <button
                            onClick={() => { onLogout(); setMenuOpen(false); }}
                            className="flex items-center w-full text-left px-3 py-2.5 text-sm font-medium rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                           >
                            <LogOut className="mr-3 h-4 w-4"/>
                            ログアウト
                           </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </header>
      
      {showCountdown && (countdownEvents.length > 0 || loadingCountdowns) && (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="fixed inset-x-0 top-[72px] z-40"
        >
            <Link 
                to="/upcoming-events"
                ref={countdownAreaRef}
                className="block bg-white/80 dark:bg-gray-900/80 backdrop-blur-md py-2 overflow-x-auto whitespace-nowrap border-b border-gray-200/50 dark:border-gray-700/50 shadow-sm no-scrollbar hover:bg-white/90 dark:hover:bg-gray-800/90 transition-colors"
            >
            <div className="flex items-center justify-start min-w-full px-4 gap-2">
                {loadingCountdowns ? (
                <div className="text-center text-xs text-gray-400 animate-pulse flex items-center">
                    <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>読み込み中...
                </div> 
                ) : (
                countdownEvents.map((event) => {
                    const { text, days } = getCountdownText(event);
                    if (!text) return null;
                    return (
                        <div
                        key={event.id}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100/80 dark:bg-slate-800/80 border border-slate-200/50 dark:border-slate-700/50 text-slate-600 dark:text-slate-300 text-xs font-semibold flex-shrink-0 shadow-sm" 
                        >
                        {days <= 0 ? (
                            <span className="flex items-center">
                                <BiSolidCalendarCheck className="mr-1 w-3.5 h-3.5 text-green-500" />
                                {text}
                            </span>
                        ) : (
                            <span className="flex items-center">
                                <Calendar className="mr-1 h-3.5 w-3.5" />
                                {text}
                            </span>
                        )}
                        </div>
                    );
                })
                )}
                <div className="pr-4"></div>
            </div>
            </Link>
        </motion.div>
      )}

      {/* Spacer to prevent content overlap */}
      <div style={{ height: `calc(72px + ${showCountdown ? countdownHeight : 0}px)` }} className="transition-[height] duration-300 ease-in-out"></div>
    </>
  );
};

export default React.memo(Header);
